/**
 * RecurringInvoicesPage — M3b Template-Verwaltung (Wiederkehrende Rechnungen)
 *
 * Allows users to create and manage recurring invoice templates.
 * Hinter Feature-Flag REACT_APP_RECURRING_ENABLED (fail-closed, Default aus).
 *
 * Features:
 *   - Liste der Vorlagen: Kunde, Intervall, nächster Lauf (nextRunAt), Status aktiv/pausiert
 *   - Template anlegen / bearbeiten via Modal
 *   - Positionen (itemsSnapshot) — eingefrorene Cent-Beträge, kein Live-Produkt-Link
 *   - Kundenwahl aus der `customers` Collection (userId-scoped)
 *   - Pausieren/Aktivieren (active-Toggle)
 *   - Lauf-Historie aus `recurring_runs` (read-only, Backend schreibt)
 *
 * HINWEIS: Die automatische Rechnungserzeugung läuft erst, wenn Daniel den
 * INFRA-1-Scheduler + RECURRING_RUN_SECRET provisioniert und
 * REACT_APP_RECURRING_ENABLED=true setzt. Bis dahin verwaltet diese Seite
 * nur Vorlagen (inert).
 *
 * KEINE "GoBD-konform"-Aussage in der UI (DoD-Auflage M3b).
 *
 * @module RecurringInvoicesPage
 */

import React, { useState, useEffect, useCallback } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useIsReadOnly } from '../hooks/useIsReadOnly';
import {
  collection,
  query,
  where,
  getDocs,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebaseConfig';
import LoadingSpinner from '../components/LoadingSpinner';
import { eurosToCents, centsToEuros } from '../utils/moneyUtils';
import { formatDate as formatDateCentral } from '../utils/formatters';
import { getTodayISO, computeNextRunAt } from '../utils/recurringSchedule';
import CustomerSelect, { CustomerSelectOption } from '../components/recurring/CustomerSelect';
import {
  createTemplate,
  getTemplates,
  updateTemplate,
  deleteTemplate,
  getRecurringRuns,
} from '../api/recurringApi';
import {
  RecurringTemplate,
  RecurringRun,
  RecurringSchedule,
  RecurringInterval,
  RecurringItemSnapshot,
  CreateRecurringTemplateInput,
} from '../types/recurringTypes';

import { canUseRecurring } from '../config/recurringAccess';
import {
  getRateConstraintForCategoryCode,
  type InvoiceTaxCategoryCode,
} from '../utils/taxCategoryCatalog';
import {
  coerceItemRateToCategory,
  recurringExemptionReasonMissing,
} from '../utils/recurringTaxCoupling';

/**
 * Auswahl-Optionen für die Steuerkategorie (EN-16931 BT-151) einer Snapshot-Position.
 *
 * Der Snapshot speichert den BT-151-Code direkt (`taxCategory`). Die Auswahl ist
 * eine EXPLIZITE fachliche Wahl der Position — NICHT aus `taxRate` abgeleitet, weil
 * 0 % mehrdeutig ist (§12(3)-PV / §4 / §6a / §13b / Ausfuhr). Reihenfolge =
 * Anzeigereihenfolge; `S` (Standard 19/7 %) ist der Default für Regel-Verkäufer.
 *
 * WICHTIG (Validator-Contract, `ApiInvoiceValidator`): `E` ist ausschließlich
 * §4 (steuerfrei MIT konkreter Norm-Nummer) — NICHT §19. §19-Kleinunternehmer
 * werden server-seitig als `Z` geführt (der Mapper setzt intern `E`), daher darf
 * das Label §19 und §4 nicht vermengen (Auflage 4).
 */
const RECURRING_TAX_CATEGORY_OPTIONS: ReadonlyArray<{
  code: InvoiceTaxCategoryCode;
  label: string;
}> = [
  { code: 'S', label: 'Standard (19 / 7 %)' },
  { code: 'Z', label: 'Nullsatz § 12 Abs. 3 (PV) / Kleinunternehmer § 19' },
  { code: 'E', label: 'Steuerfrei § 4 (mit Norm-Nr.)' },
  { code: 'AE', label: 'Reverse-Charge § 13b' },
  { code: 'K', label: 'Innergem. Lieferung § 6a' },
  { code: 'G', label: 'Ausfuhr (Drittland)' },
];

/** Default-Steuerkategorie für Regel-Verkäufer (Standard, EN-16931 `S`). */
const DEFAULT_RECURRING_TAX_CATEGORY: InvoiceTaxCategoryCode = 'S';

/**
 * Baut eine leere Snapshot-Position mit korrekt gekoppeltem Satz.
 *
 * Der USt-Satz wird an die Steuerkategorie gekoppelt (Validator-Contract):
 * Nullsatz-Kategorien (Z/E/AE/K/G) → 0, Standard (`S`) → 19. So kann eine neue
 * Position nie mit einer Satz/Kategorie-Diskrepanz starten.
 *
 * @param category Gewünschte Default-Kategorie (aus Verkäuferstatus abgeleitet).
 * @param key Stabiler React-Key.
 */
function makeInitialItem(
  category: InvoiceTaxCategoryCode,
  key: string
): RecurringItemSnapshot & { _key: string } {
  const constraint = getRateConstraintForCategoryCode(category);
  return {
    _key: key,
    name: '',
    quantity: 1,
    unitPrice: 0,
    netPrice: 0,
    taxRate: constraint.ratesLocked ? 0 : 19,
    taxCategory: category,
    exemptionReason: null,
    description: null,
    unitCode: null,
  };
}


// Zugriff (Flag + Beta-Allowlist) lebt jetzt in `config/recurringAccess.ts`
// (geteilt mit Nav + Route-Registrierung) — `canUseRecurring(uid)` unten.

// ─── Local types ──────────────────────────────────────────────────────────────

/** Minimal customer shape loaded from the `customers` collection. */
interface CustomerOption {
  docId: string;
  name: string;
  companyName?: string | null;
  email?: string | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
// getTodayISO + computeNextRunAt leben jetzt in utils/recurringSchedule.ts
// (zeitzonen-konsistente String-Arithmetik, ausrichtbar/testbar ohne React).

/** Formats a Firestore Timestamp or null as a readable date string (zentrales formatDate, M-G). */
function formatTimestamp(ts: Timestamp | null | undefined): string {
  if (!ts) return '—';
  return formatDateCentral(ts);
}

/** Formats an ISO date string YYYY-MM-DD or null. */
function formatISODate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return iso;
}

/**
 * Computes a stable netPrice from unitPrice × quantity, rounded to 2 decimal
 * places via cent intermediate to avoid floating-point drift.
 */
function computeNetPrice(unitPrice: number, quantity: number): number {
  const unitCents = eurosToCents(unitPrice);
  const netCents = Math.round(unitCents * quantity);
  return centsToEuros(netCents);
}

/**
 * Rounds a EUR value to 2 decimal places via cents intermediate.
 * Eliminates floating-point representation errors before storage.
 */
function roundToCents(euros: number): number {
  return centsToEuros(eurosToCents(euros));
}

// ─── Status badge ─────────────────────────────────────────────────────────────

const RunStatusClasses: Record<string, string> = {
  success: 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] border border-[var(--tone-success-border)]',
  failed: 'bg-[var(--tone-danger-bg)] text-[var(--tone-danger-fg)] border border-[var(--tone-danger-border)]',
  skipped: 'bg-surface-inset text-secondary border border-strong',
};

// ─── Item row editor (inside modal) ──────────────────────────────────────────

interface ItemRowProps {
  item: RecurringItemSnapshot & { _key: string };
  index: number;
  onChange: (index: number, field: keyof RecurringItemSnapshot, value: string | number) => void;
  onRemove: (index: number) => void;
  t: TFunction;
}

const ItemRow: React.FC<ItemRowProps> = ({ item, index, onChange, onRemove, t }) => {
  const category = (item.taxCategory ?? 'S') as InvoiceTaxCategoryCode;
  // Satz↔Kategorie-Kopplung (Validator-Contract): Nullsatz-Kategorien sperren
  // den Satz auf 0, `S` erlaubt nur 19/7. Vorbild: InvoiceItemsForm.
  const rateConstraint = getRateConstraintForCategoryCode(category);
  const rateLocked = rateConstraint.ratesLocked;
  // Für `E` (§4 steuerfrei) ist ein Befreiungsgrund mit Norm-Nummer Pflicht.
  const isExemptParagraph4 = category === 'E';
  const exemptionMissing = recurringExemptionReasonMissing(category, item.exemptionReason);

  return (
    <div className="grid grid-cols-12 gap-2 items-start py-2 border-b border-hairline last:border-0">
      {/* Name */}
      <div className="col-span-12 sm:col-span-4">
        <input
          type="text"
          value={item.name}
          onChange={(e) => onChange(index, 'name', e.target.value)}
          placeholder={t('modal.itemName')}
          className="w-full bg-surface-inset border border-strong rounded px-2 py-1.5 text-sm text-primary placeholder:text-faint focus:outline-none focus:border-indigo-500"
          required
        />
      </div>
      {/* Quantity */}
      <div className="col-span-4 sm:col-span-2">
        <input
          type="number"
          value={item.quantity}
          onChange={(e) => onChange(index, 'quantity', parseFloat(e.target.value) || 1)}
          min="0.01"
          step="0.01"
          placeholder={t('modal.itemQty')}
          className="w-full bg-surface-inset border border-strong rounded px-2 py-1.5 text-sm text-primary focus:outline-none focus:border-indigo-500"
        />
      </div>
      {/* Unit price */}
      <div className="col-span-4 sm:col-span-2">
        <input
          type="number"
          value={item.unitPrice}
          onChange={(e) => onChange(index, 'unitPrice', parseFloat(e.target.value) || 0)}
          min="0"
          step="0.01"
          placeholder={t('modal.itemPrice')}
          className="w-full bg-surface-inset border border-strong rounded px-2 py-1.5 text-sm text-primary focus:outline-none focus:border-indigo-500"
        />
      </div>
      {/* Tax rate — an die Steuerkategorie gekoppelt. Nullsatz-Kategorien zeigen
          nur den gesperrten 0 %-Satz; `S` bietet ausschließlich 19/7 (kein 0,
          das würde der Validator als Mismatch ablehnen). */}
      <div className="col-span-4 sm:col-span-2">
        <select
          value={item.taxRate}
          onChange={(e) => onChange(index, 'taxRate', parseInt(e.target.value, 10))}
          disabled={rateLocked}
          title={rateLocked ? t('modal.taxRateLockedHint') : undefined}
          className={`w-full bg-surface-inset border border-strong rounded px-2 py-1.5 text-sm text-primary focus:outline-none focus:border-indigo-500 ${
            rateLocked ? 'opacity-60 cursor-not-allowed' : ''
          }`}
        >
          {rateLocked ? (
            <option value={0}>0 %</option>
          ) : (
            <>
              <option value={19}>19 %</option>
              <option value={7}>7 %</option>
            </>
          )}
        </select>
      </div>
      {/* Net price (read-only) */}
      <div className="col-span-8 sm:col-span-1 flex items-center">
        <span className="text-sm text-muted whitespace-nowrap">
          {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(
            computeNetPrice(item.unitPrice, item.quantity)
          )}
        </span>
      </div>
      {/* Remove */}
      <div className="col-span-4 sm:col-span-1 flex items-center justify-end">
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="text-faint hover:text-[var(--tone-danger-fg)] transition p-1"
          title={t('modal.removeItem')}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      {/* Tax category (EN-16931 BT-151) — Pflicht für die Rechnungserzeugung */}
      <div className="col-span-12 sm:col-span-6 flex items-center gap-2">
        <label
          htmlFor={`recurring-item-taxcat-${index}`}
          className="text-xs text-faint uppercase whitespace-nowrap"
        >
          {t('modal.itemTaxCategory')}
        </label>
        <select
          id={`recurring-item-taxcat-${index}`}
          value={category}
          onChange={(e) => onChange(index, 'taxCategory', e.target.value)}
          className="flex-1 bg-surface-inset border border-strong rounded px-2 py-1.5 text-sm text-primary focus:outline-none focus:border-indigo-500"
        >
          {RECURRING_TAX_CATEGORY_OPTIONS.map((opt) => (
            <option key={opt.code} value={opt.code}>
              {opt.code} — {opt.label}
            </option>
          ))}
        </select>
      </div>
      {/* §4-Befreiungsgrund (BT-121) — nur bei Kategorie `E`, dann Pflicht mit
          konkreter Norm-Nummer (Validator: exemptionReason mit ≥1 Ziffer). */}
      {isExemptParagraph4 && (
        <div className="col-span-12 sm:col-span-6 flex flex-col gap-1">
          <label
            htmlFor={`recurring-item-exemption-${index}`}
            className="text-xs text-faint uppercase whitespace-nowrap"
          >
            {t('modal.itemExemptionReason')} <span className="text-[var(--tone-danger-fg)]">*</span>
          </label>
          <input
            id={`recurring-item-exemption-${index}`}
            type="text"
            value={item.exemptionReason ?? ''}
            onChange={(e) => onChange(index, 'exemptionReason', e.target.value)}
            placeholder={t('modal.itemExemptionReasonPlaceholder')}
            className={`w-full bg-surface-inset border rounded px-2 py-1.5 text-sm text-primary placeholder:text-faint focus:outline-none focus:border-indigo-500 ${
              exemptionMissing ? 'border-amber-500' : 'border-strong'
            }`}
          />
        </div>
      )}
    </div>
  );
};

// ─── Template form modal ──────────────────────────────────────────────────────

interface TemplateModalProps {
  existing: RecurringTemplate | null;
  customers: CustomerOption[];
  /** Owner-/Tenant-Scope-UID für die Inline-Kunden-Neuanlage. */
  ownerUid: string;
  /** Callback nach Inline-Neuanlage — hebt den neuen Kunden in die Seitenliste. */
  onCustomerCreated: (customer: CustomerOption) => void;
  onClose: () => void;
  onSuccess: (id: string) => void;
}

const TemplateModal: React.FC<TemplateModalProps> = ({
  existing,
  customers,
  ownerUid,
  onCustomerCreated,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation('recurring');
  const { sellerData } = useAuth();

  // Auflage 3: Für Kleinunternehmer (§19) ist server-seitig ausschließlich `Z`
  // gültig (Validator: alle Positionen taxCategory='Z', taxRate=0). Default für
  // neue Positionen / Alt-Vorlagen ohne Kategorie daher `Z` statt `S`.
  const defaultTaxCategory: InvoiceTaxCategoryCode = sellerData?.isSmallBusiness
    ? 'Z'
    : DEFAULT_RECURRING_TAX_CATEGORY;

  // Form state
  const [customerId, setCustomerId] = useState(existing?.customerId ?? '');
  const [items, setItems] = useState<Array<RecurringItemSnapshot & { _key: string }>>(
    existing?.itemsSnapshot.length
      ? existing.itemsSnapshot.map((it, i) =>
          // Alt-Vorlagen (vor diesem Fix) tragen keine taxCategory → Verkäufer-
          // abhängigen Default setzen und den Satz an die Kategorie koppeln,
          // damit die Position beim nächsten Speichern gültig durchkommt.
          coerceItemRateToCategory({
            ...it,
            taxCategory: it.taxCategory ?? defaultTaxCategory,
            _key: `item-${i}`,
          })
        )
      : [makeInitialItem(defaultTaxCategory, 'item-0')]
  );
  const [interval, setInterval] = useState<RecurringInterval>(
    existing?.schedule.interval ?? 'monthly'
  );
  const [dayOfMonth, setDayOfMonth] = useState(
    String(existing?.schedule.dayOfMonth ?? 1)
  );
  const [startDate, setStartDate] = useState(
    existing?.schedule.startDate ?? getTodayISO()
  );
  const [endDate, setEndDate] = useState(existing?.schedule.endDate ?? '');
  const [paymentTermsDays, setPaymentTermsDays] = useState(
    String(existing?.paymentTermsDays ?? 14)
  );
  const [documentLanguage, setDocumentLanguage] = useState(
    existing?.documentLanguage ?? 'de'
  );
  const [active, setActive] = useState(existing?.active ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Item CRUD
  const handleItemChange = (
    index: number,
    field: keyof RecurringItemSnapshot,
    value: string | number
  ) => {
    setItems((prev) => {
      const updated = [...prev];
      let item = { ...updated[index], [field]: value };
      // Recompute netPrice whenever quantity or unitPrice changes
      if (field === 'quantity' || field === 'unitPrice') {
        item.netPrice = computeNetPrice(
          field === 'unitPrice' ? (value as number) : item.unitPrice,
          field === 'quantity' ? (value as number) : item.quantity
        );
      }
      // Satz↔Kategorie-Kopplung: Bei Kategoriewechsel den USt-Satz plausibilisieren
      // (Nullsatz → 0, `S` → 19 falls aktuell 0/ungültig) und einen nicht mehr
      // passenden §4-Befreiungsgrund verwerfen (nur `E` trägt ihn).
      if (field === 'taxCategory') {
        item = coerceItemRateToCategory(item);
        if (value !== 'E') {
          item.exemptionReason = null;
        }
      }
      updated[index] = item;
      return updated;
    });
  };

  const handleAddItem = () => {
    setItems((prev) => [
      ...prev,
      makeInitialItem(defaultTaxCategory, `item-${Date.now()}`),
    ]);
  };

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  // Submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!customerId) {
      setError(t('modal.errorNoCustomer'));
      return;
    }

    const validItems = items.filter((it) => it.name.trim());
    if (validItems.length === 0) {
      setError(t('modal.errorNoItems'));
      return;
    }

    // Auflage 2: Kategorie `E` (§4 steuerfrei) verlangt einen Befreiungsgrund mit
    // konkreter Norm-Nummer (mind. eine Ziffer) — sonst scheitert der Lauf
    // server-seitig mit `exemption_reason_required`. Fail-closed schon im FE.
    const exemptWithoutReason = validItems.some((it) =>
      recurringExemptionReasonMissing(it.taxCategory, it.exemptionReason)
    );
    if (exemptWithoutReason) {
      setError(t('modal.errorExemptionReason'));
      return;
    }

    const domInt = parseInt(dayOfMonth, 10);
    if (!Number.isInteger(domInt) || domInt < 1 || domInt > 28) {
      setError(t('modal.errorDayOfMonth'));
      return;
    }

    const ptDays = parseInt(paymentTermsDays, 10);
    if (!Number.isInteger(ptDays) || ptDays < 0) {
      setError(t('modal.errorPaymentTerms'));
      return;
    }

    const schedule: RecurringSchedule = {
      interval,
      dayOfMonth: domInt,
      startDate,
      endDate: endDate.trim() || null,
    };

    // Build clean snapshot — strip _key, round prices.
    // taxCategory (EN-16931 BT-151) MUSS mitgeschrieben werden: Der PHP-Run-Endpoint
    // validiert sie fail-closed; ohne sie erzeugt die Vorlage nie eine Rechnung.
    // Der USt-Satz wird final an die Kategorie gekoppelt (defense-in-depth), und
    // der §4-Befreiungsgrund nur bei `E` persistiert (sonst null).
    const snapshot: RecurringItemSnapshot[] = validItems.map(({ _key: _k, ...it }) => {
      const category = it.taxCategory ?? defaultTaxCategory;
      const coerced = coerceItemRateToCategory({ ...it, taxCategory: category });
      return {
        ...coerced,
        unitPrice: roundToCents(coerced.unitPrice),
        netPrice: computeNetPrice(coerced.unitPrice, coerced.quantity),
        taxCategory: category,
        exemptionReason: category === 'E' ? (it.exemptionReason ?? '').trim() || null : null,
      };
    });

    const input: CreateRecurringTemplateInput = {
      customerId,
      itemsSnapshot: snapshot,
      documentLanguage,
      paymentTermsDays: ptDays,
      schedule,
      nextRunAt: computeNextRunAt(schedule),
      lastRunAt: null,
      active,
      autoSend: false,
      autoDeliveryChannel: 'none',
    };

    setSaving(true);
    try {
      if (existing) {
        await updateTemplate(existing.id, input);
        onSuccess(existing.id);
      } else {
        const newId = await createTemplate(input);
        onSuccess(newId);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-70 overflow-y-auto py-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recurring-modal-title"
    >
      <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-2xl mx-4 border border-hairline">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-hairline">
          <h2 id="recurring-modal-title" className="text-lg font-semibold text-primary">
            {existing ? t('modal.titleEdit') : t('modal.titleCreate')}
          </h2>
          <button
            onClick={onClose}
            className="text-muted hover:text-primary transition"
            aria-label={t('modal.close')}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-6">
          {/* Customer */}
          <div>
            <label htmlFor="recurring-customer-select" className="block text-sm font-medium text-secondary mb-1">
              {t('modal.labelCustomer')} <span className="text-[var(--tone-danger-fg)]">*</span>
            </label>
            <CustomerSelect
              inputId="recurring-customer-select"
              customers={customers as CustomerSelectOption[]}
              value={customerId}
              onChange={setCustomerId}
              onCustomerCreated={(c) => onCustomerCreated(c as CustomerOption)}
              ownerUid={ownerUid}
              placeholder={t('modal.searchCustomer')}
              formatCreateLabel={(input) => t('modal.createCustomer', { name: input })}
              createErrorLabel={t('modal.createCustomerError')}
            />
          </div>

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-secondary">
                {t('modal.labelItems')} <span className="text-[var(--tone-danger-fg)]">*</span>
              </span>
              <button
                type="button"
                onClick={handleAddItem}
                className="text-xs px-2 py-1 bg-indigo-700 hover:bg-indigo-600 text-white rounded transition"
              >
                + {t('modal.addItem')}
              </button>
            </div>

            {/* Column headers */}
            <div className="hidden sm:grid grid-cols-12 gap-2 mb-1 px-0">
              <div className="col-span-4 text-xs text-faint uppercase">{t('modal.itemName')}</div>
              <div className="col-span-2 text-xs text-faint uppercase">{t('modal.itemQty')}</div>
              <div className="col-span-2 text-xs text-faint uppercase">{t('modal.itemPrice')}</div>
              <div className="col-span-2 text-xs text-faint uppercase">{t('modal.itemTax')}</div>
              <div className="col-span-1 text-xs text-faint uppercase">{t('modal.itemNet')}</div>
              <div className="col-span-1" />
            </div>

            <div className="border border-hairline rounded-lg overflow-hidden px-3">
              {items.map((item, index) => (
                <ItemRow
                  key={item._key}
                  item={item}
                  index={index}
                  onChange={handleItemChange}
                  onRemove={handleRemoveItem}
                  t={t}
                />
              ))}
            </div>
          </div>

          {/* Schedule */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Interval */}
            <div>
              <label htmlFor="recurring-interval" className="block text-sm font-medium text-secondary mb-1">
                {t('modal.labelInterval')}
              </label>
              <select
                id="recurring-interval"
                value={interval}
                onChange={(e) => setInterval(e.target.value as RecurringInterval)}
                className="w-full bg-surface-inset border border-strong rounded-lg px-3 py-2 text-primary focus:outline-none focus:border-indigo-500"
              >
                <option value="monthly">{t('interval.monthly')}</option>
                <option value="quarterly">{t('interval.quarterly')}</option>
                <option value="yearly">{t('interval.yearly')}</option>
              </select>
            </div>

            {/* Day of month */}
            <div>
              <label htmlFor="recurring-day-of-month" className="block text-sm font-medium text-secondary mb-1">
                {t('modal.labelDayOfMonth')}
                <span className="ml-1 text-xs text-faint">(1–28)</span>
              </label>
              <input
                id="recurring-day-of-month"
                type="number"
                value={dayOfMonth}
                onChange={(e) => setDayOfMonth(e.target.value)}
                min="1"
                max="28"
                className="w-full bg-surface-inset border border-strong rounded-lg px-3 py-2 text-primary focus:outline-none focus:border-indigo-500"
                required
              />
            </div>

            {/* Start date */}
            <div>
              <label htmlFor="recurring-start-date" className="block text-sm font-medium text-secondary mb-1">
                {t('modal.labelStartDate')}
              </label>
              <input
                id="recurring-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full bg-surface-inset border border-strong rounded-lg px-3 py-2 text-primary focus:outline-none focus:border-indigo-500"
                required
              />
            </div>

            {/* End date (optional) */}
            <div>
              <label htmlFor="recurring-end-date" className="block text-sm font-medium text-secondary mb-1">
                {t('modal.labelEndDate')}
                <span className="ml-1 text-xs text-faint">({t('modal.optional')})</span>
              </label>
              <input
                id="recurring-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full bg-surface-inset border border-strong rounded-lg px-3 py-2 text-primary focus:outline-none focus:border-indigo-500"
              />
            </div>

            {/* Payment terms */}
            <div>
              <label htmlFor="recurring-payment-terms" className="block text-sm font-medium text-secondary mb-1">
                {t('modal.labelPaymentTerms')}
              </label>
              <input
                id="recurring-payment-terms"
                type="number"
                value={paymentTermsDays}
                onChange={(e) => setPaymentTermsDays(e.target.value)}
                min="0"
                className="w-full bg-surface-inset border border-strong rounded-lg px-3 py-2 text-primary focus:outline-none focus:border-indigo-500"
                required
              />
            </div>

            {/* Language */}
            <div>
              <label htmlFor="recurring-language" className="block text-sm font-medium text-secondary mb-1">
                {t('modal.labelLanguage')}
              </label>
              <select
                id="recurring-language"
                value={documentLanguage}
                onChange={(e) => setDocumentLanguage(e.target.value)}
                className="w-full bg-surface-inset border border-strong rounded-lg px-3 py-2 text-primary focus:outline-none focus:border-indigo-500"
              >
                <option value="de">Deutsch</option>
                <option value="en">English</option>
                <option value="fr">Français</option>
                <option value="ru">Русский</option>
                <option value="pl">Polski</option>
                <option value="ro">Română</option>
              </select>
            </div>
          </div>

          {/* Active toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setActive((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                active ? 'bg-indigo-600' : 'bg-surface-hover'
              }`}
              role="switch"
              aria-checked={active}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  active ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-secondary">
              {active ? t('modal.activeLabel') : t('modal.pausedLabel')}
            </span>
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-[var(--tone-danger-fg)] bg-[var(--tone-danger-bg)] rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-secondary hover:text-primary transition"
            >
              {t('modal.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 disabled:cursor-not-allowed text-white rounded-lg transition"
            >
              {saving ? t('modal.saving') : t('modal.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Run history section ──────────────────────────────────────────────────────

interface RunHistoryProps {
  templateId: string;
  t: TFunction;
}

const RunHistory: React.FC<RunHistoryProps> = ({ templateId, t }) => {
  const [runs, setRuns] = useState<RecurringRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    setLoading(true);
    setError(null);
    getRecurringRuns(templateId)
      .then(setRuns)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  }, [templateId, expanded]);

  return (
    <div className="mt-3">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="text-xs text-accent-ink hover:text-accent-ink flex items-center gap-1"
      >
        <svg
          className={`w-3 h-3 transition-transform ${expanded ? 'rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        {t('runs.showHistory')}
      </button>

      {expanded && (
        <div className="mt-2">
          {loading && (
            <div className="flex justify-center py-3">
              <LoadingSpinner />
            </div>
          )}
          {error && (
            <p className="text-xs text-[var(--tone-danger-fg)] px-2">{error}</p>
          )}
          {!loading && !error && runs.length === 0 && (
            <p className="text-xs text-faint px-2">{t('runs.empty')}</p>
          )}
          {!loading && !error && runs.length > 0 && (
            <table className="w-full text-xs mt-1">
              <thead>
                <tr className="text-faint uppercase">
                  <th className="text-left py-1 pr-3">{t('runs.colPeriod')}</th>
                  <th className="text-left py-1 pr-3">{t('runs.colStatus')}</th>
                  <th className="text-left py-1 pr-3">{t('runs.colInvoice')}</th>
                  <th className="text-left py-1">{t('runs.colRunAt')}</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr key={run.id} className="border-t border-hairline">
                    <td className="py-1 pr-3 text-secondary">{run.period}</td>
                    <td className="py-1 pr-3">
                      <span
                        className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                          RunStatusClasses[run.status] ?? ''
                        }`}
                      >
                        {t(`runs.status.${run.status}`)}
                      </span>
                      {run.reason && (
                        <span className="ml-1 text-faint" title={run.reason}>
                          ({run.reason})
                        </span>
                      )}
                    </td>
                    <td className="py-1 pr-3">
                      {run.generatedInvoiceId ? (
                        <span className="text-accent-ink font-mono text-xs">
                          {run.generatedInvoiceId.slice(0, 8)}…
                        </span>
                      ) : (
                        <span className="text-gray-600">—</span>
                      )}
                    </td>
                    <td className="py-1 text-muted">{formatTimestamp(run.runAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Template card ────────────────────────────────────────────────────────────

interface TemplateCardProps {
  template: RecurringTemplate;
  customerName: string;
  onEdit: (template: RecurringTemplate) => void;
  onToggleActive: (template: RecurringTemplate) => void;
  onDelete: (template: RecurringTemplate) => void;
  t: TFunction;
}

const TemplateCard: React.FC<TemplateCardProps> = ({
  template,
  customerName,
  onEdit,
  onToggleActive,
  onDelete,
  t,
}) => {
  // M11b-3: advisor (read-only) sieht keine Bearbeiten/Pausieren/Löschen-Aktionen.
  const isReadOnly = useIsReadOnly();
  const totalNet = template.itemsSnapshot.reduce(
    (sum, it) => sum + computeNetPrice(it.unitPrice, it.quantity),
    0
  );

  return (
    <div
      className={`bg-surface-raised border rounded-xl p-4 ${
        template.active ? 'border-hairline' : 'border-hairline opacity-70'
      }`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-medium text-primary truncate">{customerName}</h3>
          <p className="text-xs text-muted mt-0.5">
            {t(`interval.${template.schedule.interval}`)} &middot;{' '}
            {t('card.dayOfMonth', { day: template.schedule.dayOfMonth })} &middot;{' '}
            {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(totalNet)}{' '}
            {t('card.net')}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              template.active
                ? 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)] border border-[var(--tone-success-border)]'
                : 'bg-surface-inset text-muted border border-strong'
            }`}
          >
            {template.active ? t('status.active') : t('status.paused')}
          </span>
        </div>
      </div>

      {/* Next run */}
      <div className="mt-2 text-xs text-faint">
        {t('card.nextRun')}: <span className="text-secondary">{formatISODate(template.nextRunAt)}</span>
        {template.lastRunAt && (
          <>
            {' '}&middot; {t('card.lastRun')}: <span className="text-secondary">{formatISODate(template.lastRunAt)}</span>
          </>
        )}
      </div>

      {/* Run history */}
      <RunHistory templateId={template.id} t={t} />

      {/* Actions — M11b-3: für advisor (read-only) ausgeblendet */}
      {!isReadOnly && (
      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-hairline">
        <button
          onClick={() => onEdit(template)}
          className="text-xs px-3 py-1.5 bg-surface-inset hover:bg-surface-hover text-secondary rounded-lg transition"
        >
          {t('card.edit')}
        </button>
        <button
          onClick={() => onToggleActive(template)}
          className={`text-xs px-3 py-1.5 rounded-lg transition ${
            template.active
              ? 'bg-[var(--tone-warning-bg)] hover:brightness-110 text-[var(--tone-warning-fg)]'
              : 'bg-[var(--tone-success-bg)] hover:brightness-110 text-[var(--tone-success-fg)]'
          }`}
        >
          {template.active ? t('card.pause') : t('card.activate')}
        </button>
        <button
          onClick={() => onDelete(template)}
          className="text-xs px-3 py-1.5 bg-[var(--tone-danger-bg)] hover:brightness-110 text-[var(--tone-danger-fg)] rounded-lg transition ml-auto"
        >
          {t('card.delete')}
        </button>
      </div>
      )}
    </div>
  );
};

// ─── "Coming soon" placeholder ────────────────────────────────────────────────

const ComingSoonPlaceholder: React.FC = () => {
  const { t } = useTranslation('recurring');
  return (
    <div className="text-primary">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary">{t('pageTitle')}</h1>
        <p className="text-sm text-muted mt-1">{t('pageSubtitle')}</p>
      </div>
      <div className="bg-surface-raised border border-hairline rounded-xl p-8 text-center">
        <div className="text-4xl mb-4">&#x1F501;</div>
        <p className="text-secondary font-medium">{t('flagOff.title')}</p>
        <p className="text-sm text-faint mt-2">{t('flagOff.body')}</p>
      </div>
    </div>
  );
};

// ─── Delete confirmation dialog ───────────────────────────────────────────────

interface DeleteDialogProps {
  customerName: string;
  onConfirm: () => void;
  onCancel: () => void;
  t: TFunction;
}

const DeleteDialog: React.FC<DeleteDialogProps> = ({ customerName, onConfirm, onCancel, t }) => (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70"
    role="dialog"
    aria-modal="true"
  >
    <div className="bg-surface-raised border border-hairline rounded-xl shadow-2xl w-full max-w-sm mx-4 p-6">
      <h3 className="text-lg font-semibold text-primary mb-2">{t('delete.title')}</h3>
      <p className="text-sm text-secondary mb-6">
        {t('delete.confirm')} <strong>{customerName}</strong>?
      </p>
      <div className="flex justify-end gap-3">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-secondary hover:text-primary transition"
        >
          {t('delete.cancel')}
        </button>
        <button
          onClick={onConfirm}
          className="px-4 py-2 text-sm bg-red-700 hover:bg-red-600 text-white rounded-lg transition"
        >
          {t('delete.ok')}
        </button>
      </div>
    </div>
  </div>
);

// ─── Main page ────────────────────────────────────────────────────────────────

const RecurringInvoicesPage: React.FC = () => {
  // useAuth im ÄUSSEREN Component (unconditional Hook) — die Flag-/Beta-
  // Entscheidung braucht die uid, ohne die Rules-of-Hooks zu verletzen
  // (outer/inner-Split, das Bestandsmuster dieser Seite).
  const { currentUser } = useAuth();
  // Fail-closed: Placeholder, wenn kein Zugriff (weder Beta-UID noch Flag).
  if (!canUseRecurring(currentUser?.uid)) {
    return <ComingSoonPlaceholder />;
  }

  return <RecurringInvoicesPageInner />;
};

const RecurringInvoicesPageInner: React.FC = () => {
  const { t } = useTranslation('recurring');
  const { currentUser, effectiveScopeUid } = useAuth();
  // M11b-3: advisor (read-only) darf keine wiederkehrenden Vorlagen anlegen.
  const isReadOnly = useIsReadOnly();

  const [templates, setTemplates] = useState<RecurringTemplate[]>([]);
  const [customers, setCustomers] = useState<CustomerOption[]>([]);
  const [customerMap, setCustomerMap] = useState<Map<string, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<RecurringTemplate | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<RecurringTemplate | null>(null);

  // ── Data loading ─────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    try {
      // Load templates and customers in parallel
      const [tpls, customersSnap] = await Promise.all([
        getTemplates(),
        getDocs(
          query(
            collection(db, 'customers'),
            where('userId', '==', effectiveScopeUid)
          )
        ),
      ]);

      const customerList: CustomerOption[] = customersSnap.docs.map((d) => {
        const data = d.data() as { name?: string; companyName?: string; email?: string };
        return {
          docId: d.id,
          name: data.name ?? d.id,
          companyName: data.companyName ?? null,
          email: data.email ?? null,
        };
      });

      const cMap = new Map<string, string>(
        customerList.map((c) => [c.docId, c.companyName || c.name])
      );

      setTemplates(tpls);
      setCustomers(customerList);
      setCustomerMap(cMap);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [currentUser, effectiveScopeUid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Handlers ─────────────────────────────────────────────────────────────────

  const handleToggleActive = async (template: RecurringTemplate) => {
    try {
      await updateTemplate(template.id, { active: !template.active });
      setTemplates((prev) =>
        prev.map((t) => (t.id === template.id ? { ...t, active: !t.active } : t))
      );
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTemplate(deleteTarget.id);
      setTemplates((prev) => prev.filter((t) => t.id !== deleteTarget.id));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleModalSuccess = (_id: string) => {
    setShowModal(false);
    setEditingTemplate(null);
    loadData();
  };

  /**
   * Nimmt einen inline neu angelegten Kunden sofort in die Options-/Namensliste
   * auf, damit die Auswahl ohne Reload das korrekte Label zeigt.
   */
  const handleCustomerCreated = (customer: CustomerOption) => {
    setCustomers((prev) =>
      prev.some((c) => c.docId === customer.docId) ? prev : [...prev, customer]
    );
    setCustomerMap((prev) => {
      const next = new Map(prev);
      next.set(customer.docId, customer.companyName || customer.name);
      return next;
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="text-primary">
      {/* Page heading */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-primary">{t('pageTitle')}</h1>
          <p className="text-sm text-muted mt-1">{t('pageSubtitle')}</p>
        </div>
        {/* Vorlage erstellen — M11b-3: für advisor (read-only) ausgeblendet */}
        {!isReadOnly && (
        <button
          onClick={() => {
            setEditingTemplate(null);
            setShowModal(true);
          }}
          className="flex-shrink-0 px-4 py-2 text-sm font-medium bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg transition"
        >
          + {t('page.create')}
        </button>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 bg-[var(--tone-danger-bg)] border border-[var(--tone-danger-border)] rounded-xl p-4 text-[var(--tone-danger-fg)]">
          <p className="font-medium">{t('error.title')}</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={loadData}
            className="mt-2 text-sm underline text-[var(--tone-danger-fg)] hover:brightness-110"
          >
            {t('error.retry')}
          </button>
        </div>
      )}

      {/* Empty state */}
      {templates.length === 0 && !error && (
        <div className="bg-surface-raised border border-hairline rounded-xl p-8 text-center text-muted">
          <p className="font-medium text-secondary">{t('empty.title')}</p>
          <p className="text-sm mt-1">{t('empty.body')}</p>
        </div>
      )}

      {/* Template grid */}
      {templates.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map((tpl) => (
            <TemplateCard
              key={tpl.id}
              template={tpl}
              customerName={customerMap.get(tpl.customerId) ?? tpl.customerId}
              onEdit={(tmpl) => {
                setEditingTemplate(tmpl);
                setShowModal(true);
              }}
              onToggleActive={handleToggleActive}
              onDelete={(tmpl) => setDeleteTarget(tmpl)}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Create/Edit modal */}
      {showModal && (
        <TemplateModal
          existing={editingTemplate}
          customers={customers}
          ownerUid={effectiveScopeUid}
          onCustomerCreated={handleCustomerCreated}
          onClose={() => {
            setShowModal(false);
            setEditingTemplate(null);
          }}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <DeleteDialog
          customerName={customerMap.get(deleteTarget.customerId) ?? deleteTarget.customerId}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          t={t}
        />
      )}
    </div>
  );
};

export default RecurringInvoicesPage;
